/**
 * IndexedDB wrapper for offline-first contact/group storage.
 * Provides fast local reads so page navigations never wait for network.
 */

const DB_NAME = 'pik_store';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('contacts')) db.createObjectStore('contacts', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('groups')) db.createObjectStore('groups', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getAllContacts() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('contacts', 'readonly');
      const req = tx.objectStore('contacts').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch { return []; }
}

export async function getAllGroups() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('groups', 'readonly');
      const req = tx.objectStore('groups').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch { return []; }
}

export async function putContacts(contacts) {
  try {
    const db = await openDB();
    const tx = db.transaction('contacts', 'readwrite');
    const store = tx.objectStore('contacts');
    store.clear();
    contacts.forEach(c => store.put(c));
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

export async function putGroups(groups) {
  try {
    const db = await openDB();
    const tx = db.transaction('groups', 'readwrite');
    const store = tx.objectStore('groups');
    store.clear();
    groups.forEach(g => store.put(g));
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

export async function putOneContact(contact) {
  try {
    const db = await openDB();
    const tx = db.transaction('contacts', 'readwrite');
    tx.objectStore('contacts').put(contact);
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

export async function removeOneContact(id) {
  try {
    const db = await openDB();
    const tx = db.transaction('contacts', 'readwrite');
    tx.objectStore('contacts').delete(id);
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

export async function putOneGroup(group) {
  try {
    const db = await openDB();
    const tx = db.transaction('groups', 'readwrite');
    tx.objectStore('groups').put(group);
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

export async function getMeta(key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction('meta', 'readonly').objectStore('meta').get(key);
      req.onsuccess = () => resolve(req.result?.value ?? null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

export async function setMeta(key, value) {
  try {
    const db = await openDB();
    const tx = db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    return new Promise((resolve) => { tx.oncomplete = () => resolve(); tx.onerror = () => resolve(); });
  } catch { /* ignore */ }
}

const OWNER_KEY = 'owner_user_id';

// One chain so a logout wipe cannot land in the middle of another account's
// write, and a write that started for the previous account cannot finish
// after the wipe has been requested.
let opChain = Promise.resolve();
let sessionEpoch = 0;

function enqueue(fn) {
  const run = opChain.then(fn, fn);
  opChain = run.then(() => undefined, () => undefined);
  return run;
}

function txDone(tx, onComplete) {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve(onComplete());
    tx.onerror = () => resolve(null);
    tx.onabort = () => resolve(null);
  });
}

/**
 * Contacts and groups saved for this account, or null when the store
 * belongs to someone else (or has no owner yet). Missing owner is not
 * treated as the current account: an older unscoped cache must not flash
 * after a different login.
 */
export function readCacheForUser(userId) {
  if (!userId) return Promise.resolve(null);
  return enqueue(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(['contacts', 'groups', 'meta'], 'readonly');
      const metaReq = tx.objectStore('meta').get(OWNER_KEY);
      const contactsReq = tx.objectStore('contacts').getAll();
      const groupsReq = tx.objectStore('groups').getAll();
      return await txDone(tx, () => {
        const owner = metaReq.result?.value ?? null;
        if (owner !== userId) return null;
        return {
          contacts: contactsReq.result || [],
          groups: groupsReq.result || [],
        };
      });
    } catch {
      return null;
    }
  });
}

async function replaceStore(userId, contacts, groups, { contactsOnly = false, groupsOnly = false } = {}) {
  const epoch = sessionEpoch;
  return enqueue(async () => {
    if (epoch !== sessionEpoch || !userId) return false;
    try {
      const db = await openDB();
      const tx = db.transaction(['contacts', 'groups', 'meta'], 'readwrite');
      const meta = tx.objectStore('meta');
      const replacingAll = !contactsOnly && !groupsOnly;
      const ownerReq = meta.get(OWNER_KEY);
      ownerReq.onsuccess = () => {
        const owner = ownerReq.result?.value ?? null;
        if (!replacingAll && owner !== userId) {
          tx.abort();
          return;
        }
        // Requests stay on this turn so the transaction cannot commit early.
        if (!contactsOnly) {
          const store = tx.objectStore('groups');
          store.clear();
          (groups || []).forEach((row) => store.put(row));
        }
        if (!groupsOnly) {
          const store = tx.objectStore('contacts');
          store.clear();
          (contacts || []).forEach((row) => store.put(row));
        }
        meta.put({ key: OWNER_KEY, value: userId });
      };
      return (await txDone(tx, () => true)) === true;
    } catch {
      return false;
    }
  });
}

export function writeCacheForUser(userId, contacts, groups) {
  return replaceStore(userId, contacts, groups);
}

export function writeContactsForUser(userId, contacts) {
  return replaceStore(userId, contacts, null, { contactsOnly: true });
}

export function writeGroupsForUser(userId, groups) {
  return replaceStore(userId, null, groups, { groupsOnly: true });
}

export async function clearAll() {
  sessionEpoch += 1;
  return enqueue(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(['contacts', 'groups', 'meta'], 'readwrite');
      tx.objectStore('contacts').clear();
      tx.objectStore('groups').clear();
      tx.objectStore('meta').clear();
      return await txDone(tx, () => true);
    } catch {
      return false;
    }
  });
}
